import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns/subDays';
import { useCallback, useContext, useMemo, useState } from 'react';

import { AlertContext } from '../contexts/alert-context';
import type { ExceptionLog } from '../data/exception-log';
import { exceptionLogsQueryOptions } from '../query/query-options';
import { storeLayout } from '../router';
import { activeFilterCount, resetFilters } from './filter-fields';
import { useActive } from './use-active';
import { useFuseSearch } from './use-fuse-search';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

export function useExceptionLogs() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();
  const active = useActive();

  const { data: exceptionLogs = [], isLoading } = useQuery({
    ...exceptionLogsQueryOptions(store.id),
    queryFn: () => {
      return storeService.getExceptionLogsByStore(store.id).catch((error: Error) => {
        addErrorAlert(error.message);
        throw error;
      });
    },
    // No WebSocket feed for exception logs; keep an attended page fresh, but
    // stop polling once the tab is idle or hidden.
    refetchInterval: active ? REFRESH_INTERVAL_MS : false,
  });

  return {
    exceptionLogs,
    loading: exceptionLogs.length === 0 && isLoading,
  };
}

export const searchPlaceholders = [
  'Search by invoice number',
  'Search by product brand',
  'Search by barcode',
  'Search by clerk initials',
];

const exceptionLogSearchFields = [
  { name: 'invoice_number' as const, exact: true },
  { name: 'barcode' as const, exact: true },
  { name: 'brand' as const },
  { name: 'initials' as const },
  { name: 'station' as const },
  { name: 'close_num' as const },
];

const getExceptionLogKey = (log: ExceptionLog) => log.timestamp.getTime();

export type ExceptionLogFilters = {
  actions?: string[];
  initials?: string[];
  period?: number;
  query?: string;
};

/**
 * Filter fields the Filters popover controls — the fields the active-filter
 * badge counts and "Clear all" resets. `query` is excluded: the search box has
 * its own count weld.
 */
const EXCEPTION_LOG_FILTER_FIELDS: readonly (keyof ExceptionLogFilters)[] = [
  'actions',
  'initials',
  'period',
];

export function useFilteredExceptionLogs(
  logs: ExceptionLog[],
  initialFilters: ExceptionLogFilters
) {
  const [filters, setFilters] = useState<ExceptionLogFilters>(initialFilters);
  const searchFilter = useFuseSearch(
    filters.query,
    logs,
    exceptionLogSearchFields,
    getExceptionLogKey
  );

  // `period` is a scope filter: it defines the universe the page is inspecting
  // (last N days). `actions` / `initials` / `query` are the user actively
  // narrowing within that universe — so the result count reads "X / baseline"
  // with baseline = in-scope count. Mirrors `useFilteredProducts`.
  const { filteredLogs, baselineCount } = useMemo(() => {
    const now = new Date();
    const cutoff = filters.period ? subDays(now, filters.period) : undefined;
    const inScope = cutoff ? logs.filter((log) => log.timestamp >= cutoff) : logs;
    const filtered = inScope.filter((log) => {
      if (filters.actions && filters.actions.length > 0 && !filters.actions.includes(log.action)) {
        return false;
      }

      if (
        filters.initials &&
        filters.initials.length > 0 &&
        !filters.initials.includes(log.initials)
      ) {
        return false;
      }

      return searchFilter(log);
    });

    return { filteredLogs: filtered, baselineCount: inScope.length };
  }, [logs, filters, searchFilter]);

  const clearFilters = useCallback(() => {
    setFilters((current) => resetFilters(current, initialFilters, EXCEPTION_LOG_FILTER_FIELDS));
  }, [initialFilters]);

  return {
    filteredLogs,
    baselineCount,
    filters,
    setFilters,
    activeFilterCount: activeFilterCount(filters, initialFilters, EXCEPTION_LOG_FILTER_FIELDS),
    clearFilters,
  };
}
